import { useCallback, useEffect, useState } from "react";
import { useSync } from "@/lib/live";
import { toast } from "sonner";
import { api, errMsg, payText, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ExportButton } from "@/admin/AdminDashboard";
import { StatusBadge } from "@/components/Status";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { WeightsForm, initWeights, toWeights, calcTotal } from "@/components/WeightsForm";
import { BookingChat } from "@/components/BookingChat";

const STATUSES = ["Requested", "Confirmed", "Completed", "Cancelled"];

const runWithOverride = async (role, fn) => {
  try { return await fn(false); } catch (e) {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && ["kyc_required", "partner_unverified"].includes(d?.code) && role === "admin"
      && window.confirm(`${d.message}\n\nOverride as admin and continue anyway?`)) return fn(true);
    throw e;
  }
};

const FilterSelect = ({ value, onChange, options, label, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-44 rounded-xl bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{label}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const CompleteDialog = ({ booking, materials, role, onClose, onDone }) => {
  const [w, setW] = useState({});
  const [amount, setAmount] = useState("");
  const [pay, setPay] = useState("Pending");
  const [ref, setRef] = useState("");
  useEffect(() => { setW(initWeights((booking?.scrap_types || []).filter((t) => materials.some((m) => m.name === t)))); setAmount(""); setPay("Pending"); setRef(""); }, [booking]); // eslint-disable-line
  const save = async () => {
    try {
      await runWithOverride(role, (override) => api.patch(`/admin/bookings/${booking.booking_id}/status`, { status: "Completed", actual_weights: toWeights(w),
        amount_paid: amount === "" ? null : Number(amount), payment_status: pay, payment_ref: ref, override }));
      toast.success("Marked completed");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="complete-dialog">
        <DialogHeader><DialogTitle>Complete {booking?.booking_id}: actual weight (kg)</DialogTitle></DialogHeader>
        <WeightsForm value={w} onChange={setW} materials={materials} />
        <label className="flex items-center justify-between gap-3 border-t pt-3"><span className="text-sm font-semibold">Change amount (optional, auto ₹{calcTotal(w, materials).toFixed(2)})</span>
          <Input type="number" min="0" className="h-10 w-32" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="amount-paid-input" /></label>
        <div className="flex items-center gap-3">
          <select value={pay} onChange={(e) => setPay(e.target.value)} className="h-10 rounded-md border px-2 text-sm" data-testid="complete-payment-select">
            <option value="Pending">Payment pending</option><option value="Paid">Already paid</option>
          </select>
          {pay === "Paid" && <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Payment ref (optional)" className="h-10" data-testid="complete-payment-ref" />}
        </div>
        <DialogFooter><button onClick={save} className="h-11 rounded-xl bg-[#2E9E6B] px-6 font-bold text-white" data-testid="complete-save-btn">Save & complete</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ChatDialog = ({ booking, onClose, onSent }) => (
  <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="rounded-3xl" data-testid="admin-chat-dialog">
      <DialogHeader><DialogTitle>Messages · {booking?.booking_id}</DialogTitle></DialogHeader>
      <p className="text-xs text-[#4A6358]">Customer, pickup partner and your team all see this thread.</p>
      {booking && <BookingChat messages={booking.messages || []} me="support" postUrl={`/admin/bookings/${booking.booking_id}/messages`} onSent={onSent} testid="admin-chat" />}
    </DialogContent>
  </Dialog>
);

export const AdminBookings = ({ role = "admin" }) => {
  const { meta } = useApp();
  const ro = role === "support";
  const [chat, setChat] = useState(null);
  const [materials, setMaterials] = useState([]);
  useEffect(() => { api.get("/admin/materials").then((r) => setMaterials(r.data)).catch(() => {}); }, []);
  const [f, setF] = useState({ date_from: "", date_to: "", customer_type: "all", status: "all", flagged: false });
  const [rows, setRows] = useState([]);
  const [completing, setCompleting] = useState(null);
  const [staff, setStaff] = useState([]);
  useEffect(() => { if (!ro) api.get("/admin/staff").then((r) => setStaff(r.data.filter((s) => s.active))).catch(() => {}); }, [ro]);
  const setPay = async (b, status) => {
    const ref = status === "Paid" ? (window.prompt("Payment reference (UPI / bank ref, optional)", b.payment_ref || "") ?? null) : "";
    if (ref === null) return;
    try { await api.patch(`/admin/bookings/${b.booking_id}/payment`, { status, ref }); toast.success(`Payment marked ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const assign = async (b, sid) => {
    try {
      await runWithOverride(role, (override) => api.patch(`/admin/bookings/${b.booking_id}/assign`, { staff_id: sid === "none" ? "" : sid, override }));
      toast.success(sid === "none" ? "Partner removed" : "Partner assigned and customer notified");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));
    api.get("/admin/bookings", { params: p }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useSync(load);

  const changeStatus = async (b, status) => {
    if (status === "Completed") return setCompleting(b);
    try { await runWithOverride(role, (override) => api.patch(`/admin/bookings/${b.booking_id}/status`, { status, override })); toast.success(`Status set to ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Permanently delete booking ${b.booking_id}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/bookings/${b.booking_id}`); toast.success("Booking deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const H = ["Booking ID", "Status", "Pickup partner", "Messages", "Fraud checks", "Customer type", "Name", "Business", "Mobile", "Address", "Landmark", "Map", "Scrap types", "Est. kg", "Actual kg", "Pickup", "Payment", "Note", "Photo", "Created"];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold">From<Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 w-40 rounded-xl bg-white" data-testid="admin-filter-date-from" /></label>
        <label className="text-xs font-semibold">To<Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 w-40 rounded-xl bg-white" data-testid="admin-filter-date-to" /></label>
        <FilterSelect value={f.customer_type} onChange={(v) => setF({ ...f, customer_type: v })} options={meta?.customer_types || []} label="All customer types" testid="admin-filter-ctype" />
        <FilterSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUSES} label="All statuses" testid="admin-filter-status" />
        <label className="flex h-10 items-center gap-2 rounded-xl border border-[#D2E0D8] bg-white px-3 text-sm font-semibold">
          <input type="checkbox" checked={f.flagged} onChange={(e) => setF({ ...f, flagged: e.target.checked })} data-testid="admin-filter-flagged" /> Flagged only
        </label>
        <span className="flex-1" />
        <ExportButton kind="bookings" params={f} />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[#D2E0D8] bg-white">
        <table className="w-full min-w-[1800px] text-left text-sm" data-testid="admin-bookings-table">
          <thead className="bg-[#EAEFEA] text-xs uppercase text-[#4A6358]"><tr>{H.map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.booking_id} className="border-t border-[#EAEFEA] align-top" data-testid={`admin-booking-row-${b.booking_id}`}>
                <td className="px-3 py-3 font-bold">{b.booking_id}{b.cancelled_by && <div className="text-[10px] text-[#E05C3A]">cancelled by {b.cancelled_by}{b.cancel_reason ? `: ${b.cancel_reason}` : ""}</div>}
                  {b.kyc_override && <div className="text-[10px] text-[#92400E]">KYC overridden by admin</div>}
                  {b.status === "Completed" && <a href={`${api.defaults.baseURL}/admin/bookings/${b.booking_id}/receipt`} className="block text-xs font-semibold text-[#2E9E6B] underline" data-testid={`admin-receipt-${b.booking_id}`}>Receipt PDF</a>}
                  {role === "admin" && <button onClick={() => remove(b)} className="block text-xs font-semibold text-[#991B1B]" data-testid={`admin-delete-booking-${b.booking_id}`}>Delete</button>}</td>
                <td className="px-3 py-3">
                  <Select value={b.status} onValueChange={(v) => changeStatus(b, v)} disabled={ro}>
                    <SelectTrigger className="h-9 w-36" data-testid={`admin-status-select-${b.booking_id}`}><StatusBadge status={b.status} /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3">
                  <Select value={b.staff?.staff_id || "none"} onValueChange={(v) => assign(b, v)} disabled={ro}>
                    <SelectTrigger className="h-9 w-44" data-testid={`admin-assign-select-${b.booking_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not assigned</SelectItem>
                      {b.staff && !staff.some((s) => s.staff_id === b.staff.staff_id) && <SelectItem value={b.staff.staff_id}>{b.staff.name}</SelectItem>}
                      {staff.map((s) => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}{!s.ready ? " (not verified)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!staff.length && !ro && <div className="mt-1 text-[10px] text-[#7B968B]">Add partners in the Pickup partners tab</div>}
                  {b.staff && ro && <div className="mt-1 text-xs">{b.staff.mobile}</div>}
                  {b.trip_status && <div className="mt-1"><StatusBadge status={b.trip_status} testid={`admin-trip-${b.booking_id}`} /></div>}
                </td>
                <td className="px-3 py-3">
                  <button onClick={() => setChat(b)} data-testid={`admin-chat-btn-${b.booking_id}`}
                    className={`whitespace-nowrap rounded-lg px-3 py-1 text-xs font-bold ${["customer", "partner"].includes(b.last_msg_from) ? "bg-[#E05C3A] text-white" : "border border-[#D2E0D8]"}`}>
                    Chat ({(b.messages || []).length})</button>
                </td>
                <td className="min-w-[200px] px-3 py-3" data-testid={`admin-risk-${b.booking_id}`}>
                  {b.blocked && <div className="mb-1 rounded bg-[#991B1B] px-2 py-0.5 text-[11px] font-bold text-white">Customer blocked</div>}
                  {b.verified && <div className="mb-1 text-[11px] font-bold text-[#065F46]">Google-verified email</div>}
                  <div className={`mb-1 text-[11px] font-bold ${b.kyc_status === "Approved" ? "text-[#065F46]" : "text-[#92400E]"}`} data-testid={`admin-kyc-${b.booking_id}`}>KYC: {b.kyc_status === "Pending" ? "Pending verification" : b.kyc_status}</div>
                  {b.risk_flags?.length ? b.risk_flags.map((r) => <div key={r} className="mb-1 rounded bg-[#FEE2E2] px-2 py-0.5 text-[11px] text-[#991B1B]">{r}</div>)
                    : <span className="text-[11px] text-[#065F46]">No warnings</span>}
                </td>
                <td className="px-3 py-3">{b.customer_type}</td>
                <td className="px-3 py-3">{b.name}{b.contact_person && <div className="text-xs text-[#7B968B]">{b.contact_person}</div>}</td>
                <td className="px-3 py-3">{b.business_name || "—"}</td>
                <td className="px-3 py-3 whitespace-nowrap">{b.mobile}{b.alt_mobile && <div className="text-xs">{b.alt_mobile}</div>}</td>
                <td className="max-w-[220px] px-3 py-3">{b.address}</td>
                <td className="px-3 py-3">{b.landmark || "—"}</td>
                <td className="px-3 py-3"><a href={b.map_link} target="_blank" rel="noreferrer" className="font-semibold text-[#2E9E6B] underline">Open</a></td>
                <td className="px-3 py-3">{b.scrap_types.join(", ")}{b.other_text && ` (${b.other_text})`}</td>
                <td className="px-3 py-3">{b.est_weight ?? "—"}</td>
                <td className="px-3 py-3">{b.status === "Completed" ? <div title={JSON.stringify(b.actual_weights)}>{b.total_kg}{b.amount_paid ? <div className="text-xs text-[#065F46]">₹{b.amount_paid}</div> : null}
                  <Select value={b.payment_status || "Pending"} onValueChange={(v) => setPay(b, v)} disabled={ro}>
                    <SelectTrigger className="mt-1 h-8 w-28" data-testid={`admin-payment-select-${b.booking_id}`}><StatusBadge status={b.payment_status || "Pending"} /></SelectTrigger>
                    <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Paid">Paid</SelectItem></SelectContent>
                  </Select></div> : "—"}</td>
                <td className="px-3 py-3 whitespace-nowrap">{b.date}<div className="text-xs">{b.slot_label}</div></td>
                <td className="px-3 py-3">{payText(b.payment)}{b.payment.ifsc && <div className="text-xs">{b.payment.ifsc}</div>}</td>
                <td className="max-w-[180px] px-3 py-3">{b.note || "—"}</td>
                <td className="px-3 py-3">{b.photo_path ? <a href={fileUrl(b.photo_path)} target="_blank" rel="noreferrer" className="text-[#2E9E6B] underline">View</a> : "—"}</td>
                <td className="px-3 py-3 whitespace-nowrap text-xs">{new Date(b.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={H.length} className="p-8 text-center text-[#7B968B]">No bookings match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <CompleteDialog booking={completing} materials={materials} role={role} onClose={() => setCompleting(null)} onDone={() => { setCompleting(null); load(); }} />
      <ChatDialog booking={chat} onClose={() => setChat(null)} onSent={(messages) => { setChat((c) => ({ ...c, messages })); load(); }} />
    </div>
  );
};
